import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Input, Segmented, Select } from 'antd';
import ServiceState from './ServiceState';
import ServiceActions from './ServiceActions';
import BlockedRequests from './BlockedRequests';
import useProjectServices from '../../hooks/useProjectServices';
import useRunnables from '../../hooks/useRunnables';
import {
  formatUptime, getServiceStatus, logsStreamUrl, serviceState, sessionIdFor,
} from '../../services/SandboxService';
import { parseRunnableName, pinnedItems } from '../../utils/runnablesModel';
import './sandbox.css';
import './ServiceLogs.css';

const MAX_LINES = 2000;
/** Opened at a moment (Traces → Open logs): the lines this long before and after it. */
export const AROUND_MS = 5000;

/** The lines within AROUND_MS of `at` (epoch ms), every type: what a request that failed then saw. */
export function linesAround(lines, at, aroundMs = AROUND_MS) {
  return lines.filter((l) => {
    const time = Date.parse(l.timestamp);
    return Number.isFinite(time) && Math.abs(time - at) <= aroundMs;
  });
}

/**
 * A project's Logs view (sandbox-first, docs/SANDBOX_FIRST.md): one tab per service, its build or running output
 * live from the preview-runner's stream, a filter, and the service's controls. Works without chat or MCP.
 *
 * The stream's first event carries what the runner kept (`build`, `stdout`, `stderr`), then one event per line;
 * build lines are typed `build`. An older runner sends build lines as `stdout`: they then show under Running.
 * `around` (epoch ms, from Traces → Open logs): only the lines of that moment (±5 s, build and running), from the
 * runner's timed replay, until "Show all".
 */
// Up to this many services the tabs fit on a line; past it a searchable list takes their place.
const MAX_SERVICE_TABS = 5;

/**
 * The sessions the view lists: each service, then the files pinned in it (utils/runnablesModel `pinnedItems`, run as
 * sessions of their own, `services/app--compose-docker-compose-yaml`) and any such session the runner still has
 * (unpinned since). `[{ name, label, runnable }]`, `label` as Apps names it ("app · docker-compose.yaml").
 */
export function logSessions(serviceNames, pinned = [], runnerServices = []) {
  const short = (name) => String(name).replace(/^services\//, '');
  const runnables = [
    ...pinned.map((item) => ({ name: item.serviceName, owner: item.service, label: `${short(item.service)} · ${item.file}`, runnable: item })),
    ...runnerServices.filter((entry) => parseRunnableName(entry.serviceName) && !pinned.some((item) => item.serviceName === entry.serviceName))
      .map((entry) => {
        const { service, kind } = parseRunnableName(entry.serviceName);
        return { name: entry.serviceName, owner: service, label: `${short(service)} · ${kind}`, runnable: null };
      }),
  ];
  const listed = serviceNames.flatMap((name) => [
    { name, label: name, runnable: null },
    ...runnables.filter((item) => item.owner === name),
  ]);
  runnables.filter((item) => !serviceNames.includes(item.owner)).forEach((item) => listed.push(item));
  return listed.filter((item, index) => listed.findIndex((other) => other.name === item.name) === index)
    .map(({ name, label, runnable }) => ({ name, label, runnable }));
}

/** The lines the runner's status kept (`buildLogs`, `recentLogs.stdout` / `.stderr`), as the stream's would be. */
export function statusLines(info, now = new Date().toISOString()) {
  if (!info) { return []; }
  const text = (line) => (typeof line === 'string' ? line : line?.message ?? line?.line ?? '');
  return [
    ...(info.buildLogs || []).map((line) => ({ type: 'build', line: text(line), timestamp: line?.timestamp || now })),
    ...(info.recentLogs?.stdout || []).map((line) => ({ type: 'stdout', line: text(line), timestamp: now })),
    ...(info.recentLogs?.stderr || []).map((line) => ({ type: 'stderr', line: text(line), timestamp: now })),
  ];
}

export default function ServiceLogs({ projectId, serviceNames: ownNames, initialService = null, onServiceChange, around = null }) {
  // The project's services from the pod it runs in (its owner's, per person); a viewer reads the logs, no controls.
  const { services: projectServices, canRun, refresh } = useProjectServices(projectId, { intervalMs: 5000 });
  // The files pinned in the services (a compose file, a Dockerfile) run as sessions of their own: listed too.
  const runnables = useRunnables(projectId);
  const pinned = useMemo(() => pinnedItems(runnables.scan, runnables.choices), [runnables.scan, runnables.choices]);
  const sessions = useMemo(() => logSessions(ownNames, pinned, projectServices), [ownNames, pinned, projectServices]);
  const serviceNames = useMemo(() => sessions.map((item) => item.name), [sessions]);
  const labelOf = (name) => sessions.find((item) => item.name === name)?.label || name;
  const [service, setService] = useState(initialService && serviceNames.includes(initialService) ? initialService : serviceNames[0] || null);
  const [kind, setKind] = useState('run');
  const [filter, setFilter] = useState('');
  const [lines, setLines] = useState([]);
  const [connected, setConnected] = useState(false);
  const [at, setAt] = useState(around);
  useEffect(() => { setAt(around); }, [around]);
  const boxRef = useRef(null);
  const stickToBottom = useRef(true);
  const entry = projectServices.find((s) => s.serviceName === service) || null;

  useEffect(() => {
    // The services arrive after the first render on a cold load: the one asked for, else the first.
    if (!service && serviceNames.length) { setService(initialService && serviceNames.includes(initialService) ? initialService : serviceNames[0]); }
  }, [service, serviceNames, initialService]);

  // A Logs button for another service while this view is open (or just opened on a stale URL): follow it. A pinned
  // file's session is known once the runnables are read: followed then too.
  useEffect(() => {
    if (initialService && serviceNames.includes(initialService)) { setService(initialService); }
  }, [initialService, serviceNames.includes(initialService)]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!service) { return undefined; }
    setLines([]);
    setConnected(false);
    const source = new EventSource(logsStreamUrl(sessionIdFor(projectId, service)), { withCredentials: true });
    const push = (batch) => setLines((prev) => {
      const next = prev.concat(batch);
      return next.length > MAX_LINES ? next.slice(next.length - MAX_LINES) : next;
    });
    source.onopen = () => setConnected(true);
    source.onerror = () => setConnected(false);
    // What the runner's status kept, until the stream says (a session whose stream does not open, e.g. one that failed
    // before it ran, still shows its build and its output).
    let streamed = false;
    getServiceStatus(sessionIdFor(projectId, service))
      .then((info) => { if (!streamed) { const kept = statusLines(info); if (kept.length) { setLines(kept.slice(-MAX_LINES)); } } })
      .catch(() => {});
    source.onmessage = (event) => {
      let data;
      try { data = JSON.parse(event.data); } catch { return; }
      if (data.type === 'initial') {
        streamed = true;
        setLines([]);
        const now = new Date().toISOString();
        // with their real times (a runner that keeps them): what "around a moment" needs
        if (Array.isArray(data.timed) && data.timed.length) {
          setLines(data.timed.map((l) => ({ type: l.type, line: l.line, timestamp: l.timestamp })).slice(-MAX_LINES));
          return;
        }
        push([
          ...(data.build || []).map((line) => ({ type: 'build', line: typeof line === 'string' ? line : line?.message || '', timestamp: line?.timestamp || now })),
          ...(data.stdout || []).map((line) => ({ type: 'stdout', line, timestamp: now })),
          ...(data.stderr || []).map((line) => ({ type: 'stderr', line, timestamp: now })),
        ]);
      } else if (data.line !== undefined) {
        streamed = true;
        push([{ type: data.type, line: data.line, timestamp: data.timestamp }]);
      }
    };
    return () => source.close();
  }, [projectId, service]);

  const shown = useMemo(() => {
    const wanted = filter.trim().toLowerCase();
    const pool = at ? linesAround(lines, at) : lines.filter((l) => (kind === 'build' ? l.type === 'build' : l.type !== 'build'));
    return pool.filter((l) => !wanted || String(l.line).toLowerCase().includes(wanted));
  }, [lines, kind, filter, at]);

  useEffect(() => {
    const box = boxRef.current;
    if (box && stickToBottom.current) { box.scrollTop = box.scrollHeight; }
  }, [shown]);

  const download = () => {
    const text = shown.map((l) => `${l.timestamp || ''} ${l.type} ${l.line}`).join('\n');
    const url = URL.createObjectURL(new Blob([text], { type: 'text/plain' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = `${projectId}-${String(service).replace(/\//g, '-')}-${kind}.log`;
    link.click();
    URL.revokeObjectURL(url);
  };

  if (!serviceNames.length) {
    return <div className="svc-logs-empty">This project has no services yet, so there are no logs to show.</div>;
  }

  return (
    <div className="svc-logs">
      <div className="svc-logs-bar">
        {serviceNames.length > MAX_SERVICE_TABS ? (
          <Select
            aria-label="Service"
            className="svc-logs-service-select sbx-mono"
            onChange={(value) => { setService(value); onServiceChange?.(value); }}
            options={serviceNames.map((name) => ({ value: name, label: labelOf(name) }))}
            showSearch
            value={service}
          />
        ) : (
          <Segmented
            onChange={(value) => { setService(value); onServiceChange?.(value); }}
            options={serviceNames.map((name) => ({ value: name, label: <span className="sbx-mono" data-testid={`svc-logs-tab-${name}`}>{labelOf(name)}</span> }))}
            value={service}
          />
        )}
        <Segmented
          onChange={setKind}
          options={[{ value: 'run', label: 'Running' }, { value: 'build', label: 'Build' }]}
          value={kind}
        />
        <Input allowClear aria-label="Filter lines" className="svc-logs-filter" onChange={(e) => setFilter(e.target.value)} placeholder="Filter lines (e.g. error)" value={filter} />
        <span className="svc-logs-right">
          <span className={`sbx-pill ${connected ? 'sbx-pill-running' : ''}`}>{connected ? 'Live' : 'Not connected'}</span>
          <Button disabled={!shown.length} onClick={download} size="small">Download</Button>
          {canRun && <ServiceActions onChanged={refresh} projectId={projectId} runnable={sessions.find((item) => item.name === service)?.runnable || null} service={entry} serviceName={service} />}
        </span>
      </div>
      <BlockedRequests />
      {at && (
        <div className="svc-logs-around" data-testid="svc-logs-around" role="status">
          <span>Lines around {new Date(at).toLocaleTimeString()} (5 s before and after), build and running</span>
          <Button onClick={() => setAt(null)} size="small">Show all</Button>
        </div>
      )}
      <div className="svc-logs-summary">
        <strong>{labelOf(service)}</strong>
        <ServiceState service={entry} />
        {/* Uptime only while it runs: a stopped service is not "up 43 min". */}
        {entry?.startedAt && serviceState(entry) === 'running' && <span data-testid="svc-logs-uptime">up {formatUptime(entry.startedAt)}</span>}
      </div>
      <div
        aria-label="Log lines"
        aria-live="polite"
        className="svc-logs-box"
        onScroll={(e) => {
          const box = e.currentTarget;
          stickToBottom.current = box.scrollHeight - box.scrollTop - box.clientHeight < 40;
        }}
        ref={boxRef}
        role="log"
      >
        {shown.length === 0 ? (
          <span className="svc-logs-none">{at ? 'Nothing printed in the 5 seconds around that moment.' : kind === 'build' ? 'No build output yet.' : 'No output yet. Start the service to see what it prints.'}</span>
        ) : shown.map((l, index) => (
          <div className={`svc-logs-line svc-logs-${l.type}`} key={`${index}-${l.timestamp}`}>
            <span className="svc-logs-time">{l.timestamp ? new Date(l.timestamp).toLocaleTimeString() : ''}</span>
            <span className="svc-logs-text">{l.line}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

ServiceLogs.propTypes = {
  projectId: PropTypes.string.isRequired,
  serviceNames: PropTypes.arrayOf(PropTypes.string).isRequired,
  initialService: PropTypes.string,
  onServiceChange: PropTypes.func,
  // epoch ms: show only the lines around that moment (Traces → Open logs), until "Show all"
  around: PropTypes.number,
};
